import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import {
    Target, User, Clock, Play, AlertTriangle, AlertOctagon, Info, Lightbulb,
    Paperclip, FolderClosed, Lock, PenLine, Check, CheckCircle2, CornerDownRight,
    Loader2, ImageOff, ZoomIn, X, Monitor,
} from 'lucide-react';

// ============================================================================
// BIBLIOTECA › MANUAIS — os blocos de um capítulo
// ============================================================================
// O manual do Felipe repete os MESMOS blocos em todo capítulo. Cada um deles é
// um `tipo` aqui, e é isso que faz o próximo manual custar só um arquivo de
// conteúdo (`conteudo/*.js`) em vez de código novo:
//
//   passo    🔵  um passo do "como fazer" (texto + lista + prints)
//   aviso    🟡🔴  atenção / crítico / importante
//   exemplo  💡  um cálculo ou uma conta demonstrada
//   nota     📎📁🔒✍  anexar, guardar, lacrar, assinar
//   secao        divisor ("Como fazer — FLEX")
//   tabela       tabela pequena (a Frequência do cap. 10)
//   imagens      prints sem passo próprio (o Caderno Financeiro preenchido)
//   paragrafo    prosa corrida (o `resumo`/`texto` do Conecta) — 1+ parágrafos
//
// ⚠️ O bloco `paragrafo` nasceu para o CONECTA (DEM-00022): o manual do Felipe é
//    todo passo/aviso/tabela, mas a newsletter tem texto corrido, que não cabia
//    em nenhum bloco. É extensão limpa — serve qualquer manual futuro com prosa.
//    A LÓGICA (quebrar prosa em parágrafos, prioridade→nível) mora no mapeamento
//    puro e testado `conecta/mapearConecta.js`; aqui o bloco só desenha.
//
// ⚠️ Bloco de tipo DESCONHECIDO não é ignorado em silêncio — ele aparece na tela
//    como aviso de conteúdo não suportado. Ignorar faria um parágrafo do manual
//    desaparecer sem que ninguém notasse, que é o pior modo de falha possível
//    num documento de procedimento.
// ============================================================================

const FONTE = "'Raleway', sans-serif";

// Cor do módulo Biblioteca — sienna/marrom. Ver docs/ARQUITETURA.md › Paleta dos módulos.
export const COR = '#92400e';
export const COR_ESCURA = '#78350f';
export const COR_CLARA = '#fef7ed';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// ============================================================================
// O TOKEN DAS IMAGENS
// ============================================================================
// ⚠️ POR QUE ISTO EXISTE: `<img src>` não manda o header `Authorization`, e os
//    prints do manual têm dado pessoal (nome de colaborador, matrícula,
//    assinatura, valor de malote) — então não podem ser estáticos em `public/`,
//    que o Nginx serve sem login. O backend emite um token curto e é ele que
//    autoriza cada imagem. Ver server/biblioteca/biblioteca-acervo.js.
//
// ⚠️ RENOVAÇÃO PROATIVA, e é ela que faz a diferença aqui: o token vale 60 min,
//    mas a sessão é de LEITURA de um documento de 82 páginas. Sem renovar, o
//    print do capítulo 14 quebraria no meio da rolagem de quem lê com atenção —
//    e o sintoma (imagem faltando, sem erro) é o que ninguém consegue reportar.
//    Renovamos a 80% da validade, enquanto a aba está aberta.
const ManualTokenContext = createContext(null);

// ⚠️ `tokenUrl` é a rota autenticada que EMITE o token. Os Manuais passam
//    `/api/biblioteca/manuais/<id>/token`; o Conecta passa
//    `/api/biblioteca/conecta/<chave>/token`. Os dois devolvem { token, expiraEm,
//    baseUrl } no mesmo formato — a única coisa que muda é de onde a imagem vem.
export function ManualTokenProvider({ tokenUrl, children }) {
    const [ctx, setCtx] = useState({ estado: 'carregando', token: '', baseUrl: '' });
    const timer = useRef(null);

    const pedirToken = useCallback(async () => {
        try {
            const r = await fetch(tokenUrl, { headers: auth() });
            if (!r.ok) throw new Error(String(r.status));
            const d = await r.json();
            setCtx({ estado: 'pronto', token: d.token, baseUrl: d.baseUrl });

            // Reagenda a renovação para 80% do tempo que ainda resta.
            const restaMs = Math.max(0, d.expiraEm * 1000 - Date.now());
            if (timer.current) clearTimeout(timer.current);
            // Piso de 1 min: se o relógio do cliente estiver adiantado, `restaMs`
            // vem pequeno ou zero, e sem o piso isto viraria um laço de fetch.
            timer.current = setTimeout(pedirToken, Math.max(60000, restaMs * 0.8));
        } catch {
            setCtx({ estado: 'erro', token: '', baseUrl: '' });
        }
    }, [tokenUrl]);

    useEffect(() => {
        pedirToken();
        return () => { if (timer.current) clearTimeout(timer.current); };
    }, [pedirToken]);

    return (
        <ManualTokenContext.Provider value={{ ...ctx, renovar: pedirToken }}>
            {children}
        </ManualTokenContext.Provider>
    );
}

// ============================================================================
// A IMAGEM
// ============================================================================
// Abre em lightbox no clique, e isso NÃO é enfeite: os prints são telas de POS
// e de FLEX com texto miúdo, exibidas aqui a ~40% do tamanho original. Sem o
// zoom, metade do manual é ilegível justamente em quem mais precisa dele — o
// gerente conferindo o passo no celular da loja.
function ImagemManual({ arquivo, legenda, onAmpliar }) {
    const { estado, token, baseUrl, renovar } = useContext(ManualTokenContext) || {};
    // Uma tentativa de renovação por imagem: sem a trava, um 403 legítimo
    // (imagem removida) viraria laço de fetch com a tela toda piscando.
    const jaTentou = useRef(false);
    const [falhou, setFalhou] = useState(false);

    if (estado === 'carregando') {
        return (
            <div style={estiloMoldura}>
                <div style={estiloPlaceholder}>
                    <Loader2 size={18} className="animate-spin" style={{ color: '#a8a29e' }} />
                </div>
            </div>
        );
    }

    if (estado === 'erro' || falhou) {
        return (
            <div style={estiloMoldura}>
                <div style={estiloPlaceholder}>
                    <ImageOff size={18} style={{ color: '#a8a29e' }} />
                    <span style={{ fontSize: 12, color: '#78716c' }}>Imagem indisponível</span>
                </div>
                {legenda && <p style={estiloLegenda}>{legenda}</p>}
            </div>
        );
    }

    const src = `${baseUrl}/${arquivo}?t=${encodeURIComponent(token)}`;

    return (
        <figure style={{ ...estiloMoldura, margin: 0 }}>
            <button
                type="button"
                onClick={() => onAmpliar({ src, legenda })}
                title="Ampliar"
                style={{
                    display: 'block', width: '100%', padding: 0, border: 'none',
                    background: '#fafaf9', cursor: 'zoom-in', position: 'relative',
                    borderRadius: 8, overflow: 'hidden',
                }}
            >
                <img
                    src={src}
                    alt={legenda || 'Print do manual'}
                    loading="lazy"
                    onError={() => {
                        // Token vencido depois de a aba ficar dormindo: renova uma vez.
                        if (!jaTentou.current) { jaTentou.current = true; renovar(); }
                        else setFalhou(true);
                    }}
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                />
                <span style={estiloLupa}><ZoomIn size={13} /></span>
            </button>
            {legenda && <figcaption style={estiloLegenda}>{legenda}</figcaption>}
        </figure>
    );
}

const estiloMoldura = {
    border: '1px solid #e7e5e4', borderRadius: 10, overflow: 'hidden',
    background: '#fff', minWidth: 0, maxWidth: 420,
};
const estiloPlaceholder = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    height: 120, background: '#fafaf9',
};
const estiloLegenda = {
    margin: 0, padding: '8px 10px', fontSize: 12, lineHeight: 1.45,
    color: '#57534e', background: '#fafaf9', borderTop: '1px solid #f5f5f4',
};
const estiloLupa = {
    position: 'absolute', right: 6, top: 6, width: 24, height: 24, borderRadius: 6,
    background: 'rgba(28,25,23,0.6)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};

/**
 * Galeria de prints de um bloco. Envolve para caber no celular.
 *
 * ⚠️ GRID COM `auto-fit`, e não flex com `flexWrap` — os dois defeitos que isso
 *    conserta aparecem juntos no capítulo "Abertura do Dia", o único com QUATRO
 *    prints no mesmo passo (as telas de manager id, senha, botões e data):
 *      · no flex, a moldura era `flex: 1 1 260px`, então a imagem sobrando numa
 *        linha ímpar CRESCIA sozinha até os 420px de teto enquanto as da linha
 *        de cima ficavam em ~265 — larguras diferentes na mesma galeria;
 *      · e o `align-items` padrão do flex é `stretch`, que esticava toda moldura
 *        até a altura da mais alta da linha. Como os prints de POS têm proporções
 *        diferentes e as legendas vão de 1 a 2 linhas, sobrava uma faixa branca
 *        embaixo da legenda das mais baixas, com cara de layout quebrado.
 *    `auto-fit` (e não `auto-fill`) porque o caso comum é UM print: com
 *    `auto-fill` ele ficaria preso na largura de uma coluna de 260 mesmo tendo
 *    espaço, e `auto-fit` colapsa as colunas vazias e o deixa ir até os 420.
 */
function Galeria({ imagens, onAmpliar }) {
    if (!imagens || imagens.length === 0) return null;
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 12, marginTop: 12, alignItems: 'start',
        }}>
            {imagens.map((img, i) => (
                <ImagemManual key={`${img.arquivo}-${i}`} {...img} onAmpliar={onAmpliar} />
            ))}
        </div>
    );
}

// ============================================================================
// BLOCOS
// ============================================================================

// Aparência de cada nível de aviso. `importante` é âmbar-suave (um "leia isto"),
// `atencao` é âmbar-forte (um "cuidado aqui") e `critico` é vermelho (um "não
// faça"). São cores SEMÂNTICAS, exceção legítima à paleta do módulo — ver
// docs/ARQUITETURA.md › Paleta dos módulos.
const AVISOS = {
    importante: { rotulo: 'Importante', icone: Info, cor: '#b45309', fundo: '#fffbeb', borda: '#fcd34d' },
    atencao: { rotulo: 'Atenção', icone: AlertTriangle, cor: '#a16207', fundo: '#fefce8', borda: '#facc15' },
    critico: { rotulo: 'Atenção', icone: AlertOctagon, cor: '#b91c1c', fundo: '#fef2f2', borda: '#fca5a5' },
};

const ICONES_NOTA = {
    anexo: { icone: Paperclip, rotulo: 'Anexar' },
    pasta: { icone: FolderClosed, rotulo: 'Guardar' },
    lacre: { icone: Lock, rotulo: 'Lacrar' },
    assinatura: { icone: PenLine, rotulo: 'Assinar' },
};

function Lista({ itens, icone }) {
    if (!itens || itens.length === 0) return null;
    const Icone = icone === 'assinatura' ? PenLine : icone === 'check' ? Check : CornerDownRight;
    return (
        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gap: 6 }}>
            {itens.map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 15, lineHeight: 1.55, color: '#292524' }}>
                    <Icone size={15} style={{ color: COR, flexShrink: 0, marginTop: 4 }} />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

function Referencia({ referencia, onIrParaCapitulo }) {
    if (!referencia) return null;
    return (
        <button
            type="button"
            onClick={() => onIrParaCapitulo(referencia.capitulo)}
            style={{
                marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', borderRadius: 8, border: `1px solid ${COR}33`,
                background: COR_CLARA, color: COR_ESCURA, fontFamily: FONTE,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
        >
            <CornerDownRight size={14} />
            {referencia.texto}
        </button>
    );
}

function Bloco({ bloco, onAmpliar, onIrParaCapitulo, numeroPasso }) {
    switch (bloco.tipo) {
        case 'secao':
            return (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    margin: '32px 0 4px', paddingBottom: 8, borderBottom: `2px solid ${COR}22`,
                }}>
                    <Play size={15} style={{ color: COR }} />
                    <h4 style={{
                        margin: 0, fontFamily: FONTE, fontSize: 15, fontWeight: 800,
                        color: COR_ESCURA, textTransform: 'uppercase', letterSpacing: 0.4,
                    }}>
                        {bloco.titulo}
                    </h4>
                </div>
            );

        case 'paragrafo': {
            // Prosa corrida. O mapeamento já entrega os parágrafos separados
            // (`paragrafos`); aceita também um `texto` único (manual escrito à mão).
            const paras = (bloco.paragrafos && bloco.paragrafos.length)
                ? bloco.paragrafos
                : (bloco.texto ? [bloco.texto] : []);
            if (paras.length === 0) return null;
            return (
                <div style={{ marginTop: 16 }}>
                    {bloco.titulo && (
                        <h5 style={{ margin: '0 0 6px', fontFamily: FONTE, fontSize: 16, fontWeight: 800, color: '#1c1917' }}>
                            {bloco.titulo}
                        </h5>
                    )}
                    {paras.map((p, i) => (
                        // `pre-line`: quebras simples do texto (bullets do resumo,
                        // versos da mensagem) viram quebra de linha na tela.
                        <p key={i} style={{ margin: i ? '10px 0 0' : 0, fontSize: 15, lineHeight: 1.65, color: '#292524', whiteSpace: 'pre-line' }}>
                            {p}
                        </p>
                    ))}
                </div>
            );
        }

        case 'passo':
            return (
                <div style={{ display: 'flex', gap: 14, marginTop: 20 }}>
                    <div style={{
                        flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                        background: COR, color: '#fff', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontFamily: FONTE, fontSize: 13, fontWeight: 800,
                    }}>
                        {numeroPasso}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        {bloco.titulo && (
                            <h5 style={{ margin: '2px 0 6px', fontFamily: FONTE, fontSize: 16, fontWeight: 800, color: '#1c1917' }}>
                                {bloco.titulo}
                            </h5>
                        )}
                        {bloco.texto && (
                            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: '#292524' }}>{bloco.texto}</p>
                        )}
                        <Lista itens={bloco.itens} icone={bloco.iconeItens} />
                        {bloco.depois && (
                            <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.65, color: '#292524' }}>{bloco.depois}</p>
                        )}
                        <Referencia referencia={bloco.referencia} onIrParaCapitulo={onIrParaCapitulo} />
                        <Galeria imagens={bloco.imagens} onAmpliar={onAmpliar} />
                    </div>
                </div>
            );

        case 'aviso': {
            const cfg = AVISOS[bloco.nivel] || AVISOS.atencao;
            const Icone = cfg.icone;
            return (
                <div style={{
                    marginTop: 18, padding: '14px 16px', borderRadius: 10,
                    background: cfg.fundo, border: `1px solid ${cfg.borda}`,
                    borderLeft: `4px solid ${cfg.cor}`,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: bloco.texto || bloco.itens ? 6 : 0 }}>
                        <Icone size={16} style={{ color: cfg.cor, flexShrink: 0 }} />
                        <span style={{
                            fontFamily: FONTE, fontSize: 12, fontWeight: 800, color: cfg.cor,
                            textTransform: 'uppercase', letterSpacing: 0.6,
                        }}>
                            {bloco.titulo || cfg.rotulo}
                        </span>
                    </div>
                    {bloco.texto && (
                        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: '#292524' }}>{bloco.texto}</p>
                    )}
                    <Lista itens={bloco.itens} />
                </div>
            );
        }

        case 'exemplo':
            return (
                <div style={{
                    marginTop: 18, padding: '14px 16px', borderRadius: 10,
                    background: '#f0f9ff', border: '1px solid #bae6fd', borderLeft: '4px solid #0284c7',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <Lightbulb size={16} style={{ color: '#0369a1' }} />
                        <span style={{
                            fontFamily: FONTE, fontSize: 12, fontWeight: 800, color: '#0369a1',
                            textTransform: 'uppercase', letterSpacing: 0.6,
                        }}>
                            {bloco.titulo || 'Exemplo'}
                        </span>
                    </div>
                    <div style={{ display: 'grid', gap: 4 }}>
                        {(bloco.linhas || []).map((l, i) => (
                            <div key={i} style={{
                                display: 'flex', justifyContent: 'space-between', gap: 16,
                                padding: '5px 0',
                                borderTop: l.destaque ? '1px solid #7dd3fc' : 'none',
                                marginTop: l.destaque ? 4 : 0,
                            }}>
                                <span style={{ fontSize: 14.5, color: '#0c4a6e', fontWeight: l.destaque ? 800 : 500 }}>
                                    {l.rotulo}
                                </span>
                                {/* tabular-nums: os valores alinham na vertical, coluna de dinheiro
                                    não pode dançar de linha para linha. */}
                                <span style={{
                                    fontSize: 14.5, color: '#0c4a6e', fontWeight: l.destaque ? 800 : 700,
                                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                                }}>
                                    {l.valor}
                                </span>
                            </div>
                        ))}
                    </div>
                    {bloco.nota && (
                        <p style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.55, color: '#075985' }}>{bloco.nota}</p>
                    )}
                </div>
            );

        case 'nota': {
            const cfg = ICONES_NOTA[bloco.icone] || ICONES_NOTA.anexo;
            const Icone = cfg.icone;
            return (
                <div style={{
                    marginTop: 16, padding: '12px 14px', borderRadius: 10,
                    background: '#fafaf9', border: '1px dashed #d6d3d1',
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                    <Icone size={16} style={{ color: COR, flexShrink: 0, marginTop: 3 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                        {bloco.texto && (
                            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: '#292524' }}>{bloco.texto}</p>
                        )}
                        <Lista itens={bloco.itens} icone={bloco.icone === 'assinatura' ? 'assinatura' : undefined} />
                    </div>
                </div>
            );
        }

        case 'tabela':
            return (
                <div style={{ marginTop: 20 }}>
                    {bloco.titulo && (
                        <h5 style={{ margin: '0 0 8px', fontFamily: FONTE, fontSize: 15, fontWeight: 800, color: '#1c1917' }}>
                            {bloco.titulo}
                        </h5>
                    )}
                    {/* Rola dentro do próprio contêiner: o corpo da página nunca
                        rola na horizontal, nem no celular. */}
                    <div style={{ overflowX: 'auto', border: '1px solid #e7e5e4', borderRadius: 10 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                            <thead>
                                <tr style={{ background: COR_CLARA }}>
                                    {(bloco.colunas || []).map((c, i) => (
                                        <th key={i} style={{
                                            textAlign: 'left', padding: '10px 12px',
                                            fontFamily: FONTE, fontSize: 12, fontWeight: 800,
                                            color: COR_ESCURA, textTransform: 'uppercase', letterSpacing: 0.4,
                                            borderBottom: '1px solid #e7e5e4',
                                        }}>
                                            {c}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(bloco.linhas || []).map((linha, i) => (
                                    <tr key={i} style={{ borderTop: i ? '1px solid #f5f5f4' : 'none' }}>
                                        {linha.map((celula, j) => (
                                            <td key={j} style={{
                                                padding: '10px 12px', fontSize: 14, lineHeight: 1.5,
                                                color: '#292524', fontWeight: j === 0 ? 700 : 400,
                                                verticalAlign: 'top',
                                            }}>
                                                {celula}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );

        case 'imagens':
            return (
                <div style={{ marginTop: 22 }}>
                    {bloco.titulo && (
                        <h5 style={{ margin: '0 0 4px', fontFamily: FONTE, fontSize: 15, fontWeight: 800, color: '#1c1917' }}>
                            {bloco.titulo}
                        </h5>
                    )}
                    <Galeria imagens={bloco.imagens} onAmpliar={onAmpliar} />
                </div>
            );

        default:
            // ⚠️ Falha VISÍVEL de propósito: bloco de tipo novo num conteúdo mais
            //    recente que este arquivo faria um parágrafo do manual sumir
            //    calado. Aqui ele aparece como lacuna, e alguém conserta.
            return (
                <div style={{
                    marginTop: 16, padding: '10px 12px', borderRadius: 8,
                    background: '#fef2f2', border: '1px solid #fca5a5',
                    fontSize: 13, color: '#b91c1c',
                }}>
                    Conteúdo não suportado por esta versão da tela (tipo “{String(bloco.tipo)}”).
                </div>
            );
    }
}

// ============================================================================
// O CAPÍTULO
// ============================================================================
export function Capitulo({ capitulo, onIrParaCapitulo }) {
    const [ampliada, setAmpliada] = useState(null);

    // Fecha o lightbox com Esc — quem lê no desktop espera isso, e sem o
    // atalho o único jeito de sair é achar o X.
    useEffect(() => {
        if (!ampliada) return undefined;
        const aoTeclar = (e) => { if (e.key === 'Escape') setAmpliada(null); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [ampliada]);

    // A numeração dos passos conta SÓ os blocos do tipo `passo` — aviso e nota
    // ficam fora, senão "passo 7" da tela não corresponde ao 4º passo do manual.
    let contadorPasso = 0;

    return (
        <article>
            {/* ---- cabeçalho do capítulo ---- */}
            <header style={{ marginBottom: 4 }}>
                <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '4px 10px', borderRadius: 999, background: COR_CLARA,
                    color: COR_ESCURA, fontFamily: FONTE, fontSize: 12, fontWeight: 800,
                    letterSpacing: 0.6, marginBottom: 10,
                }}>
                    {capitulo.anexo ? 'ANEXO' : `CAPÍTULO ${capitulo.numero}`}
                </div>
                <h2 style={{
                    margin: 0, fontFamily: FONTE, fontSize: 28, fontWeight: 800,
                    color: '#1c1917', lineHeight: 1.2,
                }}>
                    {capitulo.titulo}
                </h2>
            </header>

            {/* ---- a ficha: objetivo, responsável, quando ---- */}
            {/* ⚠️ Só aparece se houver ao menos um campo. Os manuais sempre têm
                ficha (nada muda pra eles); o Conecta não usa esses campos — sem a
                guarda, o capítulo de comunicado abriria com uma caixa vazia. */}
            {(capitulo.objetivo || capitulo.responsavel || capitulo.quando
              || (capitulo.sistemas && capitulo.sistemas.length > 0)) && (
                <div style={{
                    marginTop: 18, padding: 16, borderRadius: 12,
                    background: '#fff', border: '1px solid #e7e5e4', display: 'grid', gap: 12,
                }}>
                    {capitulo.objetivo && (
                        <FichaLinha icone={Target} rotulo="Objetivo" texto={capitulo.objetivo} />
                    )}
                    {capitulo.responsavel && (
                        <FichaLinha icone={User} rotulo="Responsável" texto={capitulo.responsavel} />
                    )}
                    {capitulo.quando && (
                        <FichaLinha icone={Clock} rotulo="Quando realizar" texto={capitulo.quando} />
                    )}
                    {capitulo.sistemas && capitulo.sistemas.length > 0 && (
                        <FichaLinha
                            icone={Monitor}
                            rotulo="Sistemas"
                            texto={capitulo.sistemas.join(' · ')}
                        />
                    )}
                </div>
            )}

            {/* ---- como fazer ---- */}
            {capitulo.blocos && capitulo.blocos.length > 0 && (
                <section style={{ marginTop: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <Play size={16} style={{ color: COR }} />
                        <h3 style={{
                            margin: 0, fontFamily: FONTE, fontSize: 17, fontWeight: 800,
                            color: COR_ESCURA, textTransform: 'uppercase', letterSpacing: 0.6,
                        }}>
                            Como fazer
                        </h3>
                    </div>
                    {capitulo.blocos.map((bloco, i) => {
                        if (bloco.tipo === 'passo') contadorPasso += 1;
                        return (
                            <Bloco
                                key={i}
                                bloco={bloco}
                                numeroPasso={contadorPasso}
                                onAmpliar={setAmpliada}
                                onIrParaCapitulo={onIrParaCapitulo}
                            />
                        );
                    })}
                </section>
            )}

            {/* ---- checklist de conclusão ---- */}
            {capitulo.conclusao && (
                <section style={{
                    marginTop: 32, padding: 18, borderRadius: 12,
                    background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #16a34a',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <CheckCircle2 size={18} style={{ color: '#15803d' }} />
                        <h4 style={{
                            margin: 0, fontFamily: FONTE, fontSize: 14, fontWeight: 800,
                            color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.6,
                        }}>
                            {capitulo.conclusao.titulo}
                        </h4>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 7 }}>
                        {capitulo.conclusao.itens.map((item, i) => (
                            <li key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 14.5, lineHeight: 1.55, color: '#14532d' }}>
                                <Check size={15} style={{ color: '#16a34a', flexShrink: 0, marginTop: 3 }} />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* ---- lightbox ---- */}
            {ampliada && (
                <div
                    onClick={() => setAmpliada(null)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(28,25,23,0.88)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', padding: 24, cursor: 'zoom-out',
                    }}
                >
                    <button
                        type="button"
                        onClick={() => setAmpliada(null)}
                        aria-label="Fechar"
                        style={{
                            position: 'absolute', top: 16, right: 16, width: 38, height: 38,
                            borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.15)',
                            color: '#fff', cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <X size={20} />
                    </button>
                    <img
                        src={ampliada.src}
                        alt={ampliada.legenda || 'Print ampliado'}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain',
                            borderRadius: 8, background: '#fff', cursor: 'default',
                        }}
                    />
                    {ampliada.legenda && (
                        <p style={{
                            margin: '14px 0 0', maxWidth: 760, textAlign: 'center',
                            color: '#e7e5e4', fontSize: 14, lineHeight: 1.5,
                        }}>
                            {ampliada.legenda}
                        </p>
                    )}
                </div>
            )}
        </article>
    );
}

function FichaLinha({ icone: Icone, rotulo, texto }) {
    return (
        <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <Icone size={16} style={{ color: COR, flexShrink: 0, marginTop: 3 }} />
            <div style={{ minWidth: 0 }}>
                <div style={{
                    fontFamily: FONTE, fontSize: 11, fontWeight: 800, color: '#a8a29e',
                    textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 2,
                }}>
                    {rotulo}
                </div>
                <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#292524' }}>{texto}</div>
            </div>
        </div>
    );
}

export default Capitulo;
